import { Suspense } from 'react';

import { Modal } from '@iwoca/orion';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { BankSelectionBody } from './BankSelectionBody';
import { LinkedAccount } from './getLinkedAccounts';
import { isReconsentStep, ReconsentBody } from './reconsent/ReconsentBody';
import {
    isAbandonableStep,
    trackDismissed,
    trackRevokeOutcome
} from './reconsent/reconsentEvents';
import { ReconsentLoading } from './reconsent/ReconsentLoading';
import { useTrackModalStep } from './reconsent/useTrackModalStep';
import { TrackingSource } from './trackingSource';
import {
    InitialRenewal,
    ModalStep,
    useBankSelectionFlow
} from './useBankSelectionFlow';
import { getCountry } from '../../../iwIntlProvider/CountryProvider';
import { clearTrueLayerParams } from '../../../iwRouter/LocationHelper';
import { ShareableLinkModal } from '../shareableLinkModal/ShareableLinkModal';

import './BankSelectionModal.stacking.css';

type BankSelectionModalProps = {
    isOpen: boolean;
    onClose: () => void;
    initialStep?: ModalStep;
    initialRenewal?: InitialRenewal;
    linkedAccounts?: LinkedAccount[];
    trackingSource?: TrackingSource;
};

// Unmounting while closed is what resets the flow — `useBankSelectionFlow` lives
// in the content component, below this boundary.
export const BankSelectionModal = (props: BankSelectionModalProps) => {
    if (!props.isOpen) return null;

    return <BankSelectionModalContent {...props} />;
};

const BankSelectionModalContent = ({
    isOpen,
    onClose,
    initialStep = 'choose',
    initialRenewal,
    linkedAccounts = [],
    trackingSource = 'unknown'
}: BankSelectionModalProps) => {
    const flow = useBankSelectionFlow(initialStep, initialRenewal ?? null);
    useTrackModalStep(flow.step, trackingSource);
    const hasLinkedAccount = linkedAccounts.length > 0;
    const { title, isDefaultTitle } = getTitle({
        currentStep: flow.step,
        hasLinkedAccount,
        isRevoking: flow.isRevoking
    });
    const subTitle = getSubTitle(flow.step);

    // Closing a success screen is completion, not abandonment.
    const handleClose = () => {
        // Without this the revoke funnel loses everyone who walks away from the
        // confirmation, rather than counting them against the clicks.
        if (flow.step === 'confirmRevoke') {
            trackRevokeOutcome({
                ob_revoke_outcome: 'closed',
                bank_name: flow.revokeTarget?.bankName
            });
        }

        if (isAbandonableStep(flow.step)) {
            trackDismissed({
                step: flow.step,
                method: 'modal_close',
                source: trackingSource
            });
        }
        onClose();
    };

    const handleDone = () => {
        clearTrueLayerParams();
        onClose();
    };

    if (flow.step === 'shareableLink') {
        return (
            <ShareableLinkModal
                isOpen={true}
                onClose={flow.goToChoose}
                trackingSource={trackingSource}
                connectionCount={linkedAccounts.length}
            />
        );
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            closeIcon={true}
            headerBar={true}
            title={title}
            subTitle={subTitle}
            data-testid="bank-selection-modal"
            className={classNames({
                '[&_[data-testid=modal-title]]:text-center': isDefaultTitle
            })}
        >
            {isReconsentStep(flow.step) ? (
                <Suspense fallback={<ReconsentLoading />}>
                    <ReconsentBody
                        flow={flow}
                        onDone={handleDone}
                        trackingSource={trackingSource}
                    />
                </Suspense>
            ) : (
                <BankSelectionBody
                    step={flow.step}
                    selectedBank={flow.selectedBank}
                    selectBank={flow.selectBank}
                    unselectBank={flow.unselectBank}
                    onLinkAnother={flow.linkAnother}
                    linkedAccounts={linkedAccounts}
                    onDone={handleDone}
                    onOpenShareableLink={flow.goToShareableLink}
                    trackingSource={trackingSource}
                />
            )}
        </Modal>
    );
};

function getTitle({
    currentStep,
    hasLinkedAccount,
    isRevoking
}: {
    currentStep: ModalStep;
    hasLinkedAccount: boolean;
    isRevoking: boolean;
}) {
    if (isReconsentStep(currentStep)) {
        return {
            isDefaultTitle: false,
            title: isRevoking ? (
                <FormattedMessage
                    id="_.bank_selection_modal.reconsent.revoke_title"
                    defaultMessage="Revoke your Open Banking connection"
                />
            ) : (
                <FormattedMessage
                    id="_.bank_selection_modal.reconsent.title"
                    defaultMessage="Extend your Open Banking connection"
                />
            )
        };
    }

    if (currentStep === 'success') {
        return {
            isDefaultTitle: false,
            title: (
                <FormattedMessage
                    id="_.bank_selection_modal.success_title"
                    defaultMessage="You have successfully linked your business bank account"
                />
            )
        };
    }

    if (currentStep === 'choose' && hasLinkedAccount && getCountry() === 'UK') {
        return {
            isDefaultTitle: false,
            title: (
                <FormattedMessage
                    id="_.bank_selection_modal.link_additional_title"
                    defaultMessage="You’re linking an additional account"
                />
            )
        };
    }

    return {
        isDefaultTitle: true,
        title: (
            <FormattedMessage
                id="_.bank_selection_modal.title"
                defaultMessage="Connect your business account"
            />
        )
    };
}

function getSubTitle(currentStep: ModalStep) {
    if (currentStep === 'success') {
        return (
            <FormattedMessage
                id="_.bank_selection_modal.success_subtitle"
                defaultMessage="You may now close this tab or link an additional account below."
            />
        );
    }

    return undefined;
}
